import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { ForumHealthMetric, ForumHealthRadarPoint } from '../../types';
import { RADAR_METRICS, FALLBACK_RADAR_CATEGORIES, formatMetricAvg } from './constants';

// Recharts rotates PolarRadiusAxis tick labels to lie along the axis line,
// which renders the scale numbers sideways. Drawing our own <text> (without
// the rotate transform) keeps them upright.
function uprightRadiusTick(fill: string, fontSize: number, fontWeight?: number) {
  return function UprightRadiusTick({ x, y, payload }: {
    x?: string | number;
    y?: string | number;
    payload?: { value: number };
  }) {
    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor="middle"
        fill={fill}
        fontSize={fontSize}
        fontWeight={fontWeight}
      >
        {payload?.value}
      </text>
    );
  };
}

// Ben's "amoeba" chart: the forum's average per category radiating from a
// center point, the selected month overlaid on the month before so shifts are
// visible. Metric picks the scale (State 1-4 or Direction 1-3).
export default function ForumHealthRadar({
  points,
  metric,
  currentLabel,
  previousLabel,
  scaleNote,
}: {
  points?: ForumHealthRadarPoint[];
  metric: ForumHealthMetric;
  currentLabel: string;
  previousLabel: string;
  // Overrides the metric's default note (which is worded for forum averages).
  scaleNote?: string;
}) {
  const { max, scaleNote: defaultScaleNote } = RADAR_METRICS[metric];
  const note = scaleNote ?? defaultScaleNote;
  const data = (points ?? []).map((p) => ({
    category: p.category,
    current: p.current_avg,
    previous: p.previous_avg,
  }));
  const hasAnyValue = data.some((d) => d.current != null || d.previous != null);
  if (!hasAnyValue) {
    // Empty state: hold the space with a blank radar (same axes/size) plus a
    // very faint midpoint series, and overlay the message on top.
    const placeholderData = (
      data.length > 0 ? data.map((d) => d.category) : FALLBACK_RADAR_CATEGORIES
    ).map((category) => ({ category, placeholder: (1 + max) / 2 }));
    return (
      <div className="relative h-80">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={placeholderData} outerRadius="70%">
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis dataKey="category" tick={{ fill: '#d1d5db', fontSize: 12 }} />
            <PolarRadiusAxis
              domain={[1, max]}
              tickCount={max}
              tick={uprightRadiusTick('#e5e7eb', 10)}
              axisLine={false}
            />
            <Radar
              name="placeholder"
              dataKey="placeholder"
              stroke="#e5e7eb"
              fill="#e5e7eb"
              fillOpacity={0.15}
              strokeWidth={1}
              isAnimationActive={false}
            />
          </RadarChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <p className="text-sm text-gray-500 bg-white/85 rounded-lg px-4 py-2 text-center max-w-xs shadow-sm">
            No snapshots for {currentLabel.toLowerCase() === 'this month' ? 'this month' : currentLabel} —
            the forum's health radar will appear here.
          </p>
        </div>
      </div>
    );
  }
  const hasCurrent = data.some((d) => d.current != null);
  return (
    <div>
      {!hasCurrent && (
        <p className="text-sm text-gray-400 italic mb-1">
          No snapshots for {currentLabel.toLowerCase() === 'this month' ? 'this month' : currentLabel} —
          showing {previousLabel.toLowerCase() === 'last month' ? 'last month' : previousLabel} only.
        </p>
      )}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="70%">
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis dataKey="category" tick={{ fill: '#4b5563', fontSize: 12 }} />
            <PolarRadiusAxis
              domain={[1, max]}
              tickCount={max}
              tick={uprightRadiusTick('#111827', 13, 700)}
              axisLine={false}
            />
            <Tooltip
              formatter={(value) => formatMetricAvg(value as number | null, metric)}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Radar
              name={previousLabel}
              dataKey="previous"
              stroke="#dc981b"
              fill="#dc981b"
              fillOpacity={0.25}
              strokeWidth={2}
            />
            <Radar
              name={currentLabel}
              dataKey="current"
              stroke="#043370"
              fill="#043370"
              fillOpacity={0.35}
              strokeWidth={2}
            />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-400 mt-1">{note}</p>
    </div>
  );
}
